/* MinibarManual.jsx — Belirli bir dönem (date_from..date_to) için
 * minibar 9 ürününün toplam tüketim adetlerini manuel girer.
 * Oda oda girmek yerine dönem toplamı olarak kaydeder; raporlarda görünür.
 */
import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Loader2, Save, Trash2, History, CalendarRange, Pencil, Check, X as XIcon } from 'lucide-react';
import { toast } from 'sonner';
import { formatTRNumber } from '../lib/utils';

export default function MinibarManual() {
    const today = new Date().toISOString().slice(0, 10);
    const [date, setDate] = useState(today);
    const [products, setProducts] = useState([]);
    const [qty, setQty] = useState({}); // code -> qty
    const [note, setNote] = useState('');
    const [records, setRecords] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        loadProducts();
        loadRecords();
    }, []);

    const loadProducts = async () => {
        try {
            const res = await api.get('/minibar/products');
            setProducts(res.data || []);
            const initQty = {};
            (res.data || []).forEach(p => { initQty[p.code] = 0; });
            setQty(initQty);
        } catch {
            toast.error('Ürünler yüklenemedi');
        }
    };

    const loadRecords = async () => {
        setLoading(true);
        try {
            const res = await api.get('/minibar/manual');
            setRecords(res.data?.items || []);
        } catch {
            toast.error('Kayıtlar yüklenemedi');
        } finally {
            setLoading(false);
        }
    };

    const computedTotal = products.reduce((s, p) => s + (Number(qty[p.code]) || 0) * (Number(p.unit_price_tl) || 0), 0);
    const totalQty = products.reduce((s, p) => s + (Number(qty[p.code]) || 0), 0);

    const handleSave = async () => {
        const items = products
            .map(p => ({ code: p.code, qty: Number(qty[p.code]) || 0 }))
            .filter(it => it.qty > 0);
        if (items.length === 0) {
            toast.error('En az bir ürün adedi > 0 olmalı');
            return;
        }
        setSaving(true);
        try {
            await api.post('/minibar/manual', {
                date,
                items,
                note: note || null,
            });
            toast.success('Manuel kayıt eklendi');
            // Form sıfırla
            const reset = {};
            products.forEach(p => { reset[p.code] = 0; });
            setQty(reset);
            setNote('');
            loadRecords();
        } catch (e) {
            const msg = e?.response?.data?.detail || 'Kayıt eklenemedi';
            toast.error(msg);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Bu manuel kaydı silmek istediğinize emin misiniz?')) return;
        try {
            await api.delete(`/minibar/manual/${id}`);
            toast.success('Kayıt silindi');
            loadRecords();
        } catch {
            toast.error('Silinemedi');
        }
    };

    // Inline edit state
    const [editingId, setEditingId] = useState(null);
    const [editDate, setEditDate] = useState('');
    const [editNote, setEditNote] = useState('');
    const [editItems, setEditItems] = useState({});  // code -> qty

    const startEdit = (r) => {
        setEditingId(r.id);
        setEditDate(r.date || r.date_to || r.date_from || today);
        setEditNote(r.note || '');
        const map = {};
        products.forEach(p => { map[p.code] = 0; });
        (r.items || []).forEach(it => { map[it.code] = it.qty; });
        setEditItems(map);
    };
    const cancelEdit = () => setEditingId(null);
    const saveRowEdit = async (id) => {
        const items = Object.entries(editItems)
            .map(([code, qty]) => ({ code, qty: Number(qty) || 0 }))
            .filter(it => it.qty > 0);
        if (items.length === 0) {
            toast.error('En az bir ürün adedi > 0 olmalı');
            return;
        }
        try {
            await api.put(`/minibar/manual/${id}`, {
                date: editDate,
                items,
                note: editNote || null,
            });
            toast.success('Güncellendi');
            setEditingId(null);
            loadRecords();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Güncelleme başarısız');
        }
    };

    return (
        <div className="space-y-4" data-testid="minibar-manual">
            <Card>
                <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-base">
                        <CalendarRange className="w-5 h-5 text-emerald-600" />
                        Manuel Geçmiş Tüketim Girişi
                    </CardTitle>
                    <p className="text-xs text-slate-500">
                        Dönem toplamı olarak girilen miktarlar raporlara dahil edilir. Oda oda girmenize gerek yok.
                    </p>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="flex items-end gap-3 flex-wrap">
                        <div>
                            <div className="text-xs text-slate-500 mb-1">Tarih</div>
                            <Input
                                type="date"
                                value={date}
                                onChange={(e) => setDate(e.target.value)}
                                className="h-9 w-40"
                                data-testid="manual-date"
                            />
                        </div>
                        <div className="flex-1 min-w-48">
                            <div className="text-xs text-slate-500 mb-1">Not (opsiyonel)</div>
                            <Input
                                value={note}
                                onChange={(e) => setNote(e.target.value)}
                                placeholder="ör. 14.05-20.06 arası dönem girişi"
                                className="h-9"
                                data-testid="manual-note"
                            />
                        </div>
                    </div>

                    {/* 9 ürün adet */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {products.map(p => (
                            <div key={p.code} className="flex items-center justify-between gap-3 p-2 border rounded-md bg-slate-50">
                                <div className="min-w-0 flex-1">
                                    <div className="text-[10px] text-slate-400 font-mono">#{p.code}</div>
                                    <div className="text-sm font-medium text-slate-800 truncate">{p.name}</div>
                                    <div className="text-xs text-slate-500">{formatTRNumber(p.unit_price_tl, 2)} ₺ / {p.unit}</div>
                                </div>
                                <Input
                                    type="number"
                                    min="0"
                                    value={qty[p.code] ?? 0}
                                    onChange={(e) => setQty({ ...qty, [p.code]: e.target.value })}
                                    className="w-24 h-9 text-center"
                                    data-testid={`manual-qty-${p.code}`}
                                />
                            </div>
                        ))}
                    </div>

                    <div className="flex items-center justify-between pt-2 border-t">
                        <div className="text-sm text-slate-600">
                            Toplam Adet: <span className="font-semibold text-slate-800" data-testid="manual-total-qty">{totalQty}</span>
                            <span className="mx-3 text-slate-300">|</span>
                            Toplam Tutar: <span className="text-lg font-bold text-emerald-700" data-testid="manual-total-tl">{formatTRNumber(computedTotal, 2)} ₺</span>
                        </div>
                        <Button
                            onClick={handleSave}
                            disabled={saving}
                            className="bg-emerald-600 hover:bg-emerald-700"
                            data-testid="manual-save"
                        >
                            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                            Kaydet
                        </Button>
                    </div>
                </CardContent>
            </Card>

            {/* Mevcut kayıtlar listesi */}
            <Card>
                <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-base">
                        <History className="w-5 h-5 text-slate-500" />
                        Manuel Kayıtlar
                        <Badge variant="outline" className="ml-2" data-testid="manual-count">{records.length}</Badge>
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    {loading ? (
                        <div className="text-center py-6 text-slate-400">
                            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
                            Yükleniyor...
                        </div>
                    ) : records.length === 0 ? (
                        <div className="text-center py-6 text-slate-400 text-sm">Henüz manuel kayıt yok</div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="bg-slate-50 border-b">
                                    <tr className="text-left text-slate-600">
                                        <th className="px-3 py-2 font-medium">Tarih</th>
                                        <th className="px-3 py-2 font-medium">Ürünler</th>
                                        <th className="px-3 py-2 font-medium text-right">Toplam Tutar</th>
                                        <th className="px-3 py-2 font-medium">Not</th>
                                        <th className="px-3 py-2 font-medium text-center">İşlem</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {records.map(r => {
                                        const isEditing = editingId === r.id;
                                        return (
                                        <tr key={r.id} className="border-b hover:bg-slate-50" data-testid={`manual-row-${r.id}`}>
                                            <td className="px-3 py-2 whitespace-nowrap font-medium text-slate-700 align-top">
                                                {isEditing ? (
                                                    <Input type="date" value={editDate} onChange={(e) => setEditDate(e.target.value)} className="h-7 w-36" data-testid={`manual-edit-date-${r.id}`} />
                                                ) : (
                                                    r.date || (r.date_from === r.date_to ? r.date_from : `${r.date_from} → ${r.date_to}`)
                                                )}
                                            </td>
                                            <td className="px-3 py-2 text-slate-600 text-xs align-top">
                                                {isEditing ? (
                                                    <div className="grid grid-cols-2 gap-1">
                                                        {products.map(p => (
                                                            <div key={p.code} className="flex items-center gap-1">
                                                                <span className="text-[10px] flex-1 truncate">{p.name}</span>
                                                                <Input
                                                                    type="number"
                                                                    min="0"
                                                                    value={editItems[p.code] ?? 0}
                                                                    onChange={(e) => setEditItems({ ...editItems, [p.code]: e.target.value })}
                                                                    className="h-6 w-14 text-center text-xs"
                                                                    data-testid={`manual-edit-item-${r.id}-${p.code}`}
                                                                />
                                                            </div>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    (r.items || []).map(it => `${it.name} (${it.qty})`).join(', ')
                                                )}
                                            </td>
                                            <td className="px-3 py-2 text-right font-semibold text-emerald-700 align-top">
                                                {formatTRNumber(r.total_tl, 2)} ₺
                                            </td>
                                            <td className="px-3 py-2 text-slate-500 text-xs italic align-top">
                                                {isEditing ? (
                                                    <Input value={editNote} onChange={(e) => setEditNote(e.target.value)} className="h-7" data-testid={`manual-edit-note-${r.id}`} />
                                                ) : (r.note || '-')}
                                            </td>
                                            <td className="px-3 py-2 text-center align-top whitespace-nowrap">
                                                {isEditing ? (
                                                    <>
                                                        <Button size="icon" variant="ghost" onClick={() => saveRowEdit(r.id)} className="h-7 w-7 text-emerald-700" data-testid={`manual-save-edit-${r.id}`}>
                                                            <Check className="w-4 h-4" />
                                                        </Button>
                                                        <Button size="icon" variant="ghost" onClick={cancelEdit} className="h-7 w-7 text-slate-500" data-testid={`manual-cancel-edit-${r.id}`}>
                                                            <XIcon className="w-4 h-4" />
                                                        </Button>
                                                    </>
                                                ) : (
                                                    <>
                                                        <Button size="icon" variant="ghost" onClick={() => startEdit(r)} className="h-7 w-7 text-blue-600" data-testid={`manual-edit-${r.id}`}>
                                                            <Pencil className="w-4 h-4" />
                                                        </Button>
                                                        <Button size="icon" variant="ghost" onClick={() => handleDelete(r.id)} className="h-7 w-7 text-red-600 hover:text-red-800 hover:bg-red-50" data-testid={`manual-delete-${r.id}`}>
                                                            <Trash2 className="w-4 h-4" />
                                                        </Button>
                                                    </>
                                                )}
                                            </td>
                                        </tr>
                                    );})}
                                </tbody>
                            </table>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
